import { brandAssetRepository } from './landingContentShared.js';
import aylinPortrait from '../../img/matching-demo/aylin.webp';
import emrePortrait from '../../img/matching-demo/emre.webp';
import selinPortrait from '../../img/matching-demo/selin.webp';
import mehmetPortrait from '../../img/matching-demo/mehmet.webp';
import zeynepPortrait from '../../img/matching-demo/zeynep.webp';
import denizPortrait from '../../img/matching-demo/deniz.webp';
import type { LandingLocale } from './landingContentTypes.js';
import type { CompanyPortalCandidateViewModel, CompanyPortalLabels } from '@vivaporta/company-portal-ui';

export interface LandingMatchingCandidate extends Omit<CompanyPortalCandidateViewModel, 'age'> {
  age: string;
}
export interface LandingMatchingContent {
  windowTitle: string;
  badge: string;
  companyName: string;
  eyebrow: string;
  initials: string;
  logoUrl: string;
  logoAlt: string;
  cobrandLabel: string;
  cobrandLogoUrl: string;
  cobrandLogoAlt: string;
  resultLabel: string;
  searchLabel: string;
  searchPlaceholder: string;
  filterLabel: string;
  resetLabel: string;
  allLabel: string;
  emptyTitle: string;
  emptyText: string;
  ctaText: string;
  experienceValue: string;
  labels: Omit<CompanyPortalLabels, 'photoAlt' | 'ageValue'> & { photoAlt: string; ageValue: string };
  candidates: LandingMatchingCandidate[];
}

const candidates: LandingMatchingCandidate[] = [
  {
    id: 'demo-aylin',
    firstName: 'Aylin',
    origin: 'Türkei',
    qualification: 'Pflegefachkraft · Anerkennung in Vorbereitung',
    age: '28',
    focusAreas: ['Pflege'],
    workExperienceYears: '5',
    germanLevel: 'B2',
    currentProfession: 'Gesundheits- und Krankenpflegerin',
    education: 'Bachelor Pflegewissenschaft',
    skills: 'Intensivpflege, Wundversorgung, Patientendokumentation',
    experience: 'Akutklinik und ambulante Pflege',
    profileImageUrl: aylinPortrait,
    documents: [
      { id: 'cv', label: 'Lebenslauf' },
      { id: 'qualification', label: 'Qualifikationsnachweis' },
    ],
  },
  {
    id: 'demo-emre',
    firstName: 'Emre',
    origin: 'Türkei',
    qualification: 'Koch · internationale Berufserfahrung',
    age: '31',
    focusAreas: ['Gastronomie'],
    workExperienceYears: '8',
    germanLevel: 'B1',
    currentProfession: 'Chef de Partie',
    education: 'Berufsausbildung Gastronomie',
    skills: 'À-la-carte, Warenplanung, HACCP',
    experience: 'Hotel- und Systemgastronomie',
    profileImageUrl: emrePortrait,
    documents: [{ id: 'cv', label: 'Lebenslauf' }],
  },
  {
    id: 'demo-selin',
    firstName: 'Selin',
    origin: 'Türkei',
    qualification: 'Verkäuferin · Schwerpunkt Frischetheke',
    age: '26',
    focusAreas: ['Frischetheke', 'Einzelhandel'],
    workExperienceYears: '4',
    germanLevel: 'B2',
    currentProfession: 'Fachverkäuferin',
    education: 'Berufsausbildung Lebensmittelhandel',
    skills: 'Kundenberatung, Warenpräsentation, Hygiene',
    experience: 'Frischetheke und Filialverkauf',
    profileImageUrl: selinPortrait,
    documents: [{ id: 'cv', label: 'Lebenslauf' }],
  },
  {
    id: 'demo-mehmet',
    firstName: 'Mehmet',
    origin: 'Türkei',
    qualification: 'Metzger · Zerlegung und Produktion',
    age: '34',
    focusAreas: ['Metzgerei', 'Zerlegung'],
    workExperienceYears: '11',
    germanLevel: 'A2',
    currentProfession: 'Fleischer',
    education: 'Berufsausbildung Fleischverarbeitung',
    skills: 'Zerlegung, Produktion, Qualitätssicherung',
    experience: 'Handwerks- und Produktionsbetrieb',
    profileImageUrl: mehmetPortrait,
    documents: [{ id: 'cv', label: 'Lebenslauf' }],
  },
  {
    id: 'demo-zeynep',
    firstName: 'Zeynep',
    origin: 'Türkei',
    qualification: 'Bäckerin · Backstube und Feinbackwaren',
    age: '29',
    focusAreas: ['Bäckerei', 'Backstube'],
    workExperienceYears: '6',
    germanLevel: 'B1',
    currentProfession: 'Bäckerin',
    education: 'Berufsausbildung Bäckereihandwerk',
    skills: 'Teigführung, Ofenarbeit, Feinbackwaren',
    experience: 'Handwerksbäckerei und Produktion',
    profileImageUrl: zeynepPortrait,
    documents: [{ id: 'cv', label: 'Lebenslauf' }],
  },
  {
    id: 'demo-deniz',
    firstName: 'Deniz',
    origin: 'Türkei',
    qualification: 'Elektroniker · Betriebstechnik',
    age: '30',
    focusAreas: ['Handwerk'],
    workExperienceYears: '7',
    germanLevel: 'B1',
    currentProfession: 'Elektroniker',
    education: 'Berufsausbildung Elektrotechnik',
    skills: 'Wartung, Schaltschrankbau, Fehlersuche',
    experience: 'Industrieanlagen und Gebäudetechnik',
    profileImageUrl: denizPortrait,
    documents: [{ id: 'cv', label: 'Lebenslauf' }],
  },
];

const labels = (locale: LandingLocale): LandingMatchingContent['labels'] =>
  locale === 'en'
    ? {
        firstName: 'First name',
        age: 'Age',
        origin: 'Origin',
        experienceYears: 'Experience',
        germanLevel: 'German',
        focusAreas: 'Focus areas',
        profileDetails: 'View profile details',
        currentProfession: 'Current profession',
        education: 'Education',
        skills: 'Skills',
        experience: 'Experience',
        documents: 'Documents',
        status: 'Interview interest',
        requestInterest: 'Send interview interest',
        interestRequested: 'Interest sent',
        withdrawQuestion: 'Withdraw interest?',
        withdrawDescription: 'You can mark the profile again at any time in this demo.',
        keepInterest: 'Keep interest',
        withdraw: 'Withdraw',
        documentUnavailable: 'Confidential documents are available only in the protected company portal.',
        photoAlt: 'Demo profile of {name}',
        ageValue: '{age} years',
      }
    : locale === 'tr'
      ? {
          firstName: 'Ad',
          age: 'Yaş',
          origin: 'Köken',
          experienceYears: 'Deneyim',
          germanLevel: 'Almanca',
          focusAreas: 'Çalışma alanları',
          profileDetails: 'Profil ayrıntıları',
          currentProfession: 'Mevcut meslek',
          education: 'Eğitim',
          skills: 'Beceriler',
          experience: 'Deneyim',
          documents: 'Belgeler',
          status: 'Görüşme talebi',
          requestInterest: 'Görüşme talebi gönder',
          interestRequested: 'Talep gönderildi',
          withdrawQuestion: 'İlgi geri çekilsin mi?',
          withdrawDescription: 'Bu demoda profili istediğin zaman tekrar işaretleyebilirsin.',
          keepInterest: 'İlgiyi koru',
          withdraw: 'Geri çek',
          documentUnavailable: 'Gizli belgeler yalnızca korumalı şirket portalında kullanılabilir.',
          photoAlt: '{name} demo profili',
          ageValue: '{age} yaş',
        }
      : {
          firstName: 'Vorname',
          age: 'Alter',
          origin: 'Herkunft',
          experienceYears: 'Berufserfahrung',
          germanLevel: 'Deutsch',
          focusAreas: 'Tätigkeitsbereiche',
          profileDetails: 'Profildetails anzeigen',
          currentProfession: 'Aktueller Beruf',
          education: 'Ausbildung',
          skills: 'Kenntnisse',
          experience: 'Erfahrung',
          documents: 'Dokumente',
          status: 'Gesprächsinteresse',
          requestInterest: 'Gesprächsinteresse senden',
          interestRequested: 'Interesse gesendet',
          withdrawQuestion: 'Gesprächsinteresse zurücknehmen?',
          withdrawDescription: 'Du kannst das Profil in dieser Demo jederzeit erneut vormerken.',
          keepInterest: 'Interesse behalten',
          withdraw: 'Zurücknehmen',
          documentUnavailable: 'Vertrauliche Unterlagen sind nur im geschützten Unternehmensportal verfügbar.',
          photoAlt: 'Demo-Profil von {name}',
          ageValue: '{age} Jahre',
        };

const ui = {
  de: {
    windowTitle: 'Viva Porta Matching · Interaktive Demo',
    badge: 'Demo-Modus',
    eyebrow: 'Unternehmensportal',
    cobrandLabel: 'in Zusammenarbeit mit',
    resultLabel: 'passende Profile',
    searchLabel: 'Profile durchsuchen',
    searchPlaceholder: 'Name, Qualifikation oder Herkunft',
    filterLabel: 'Tätigkeitsbereich',
    resetLabel: 'Filter zurücksetzen',
    allLabel: 'Alle',
    emptyTitle: 'Keine passenden Profile',
    emptyText: 'Passe deine Suche oder den Tätigkeitsfilter an.',
    ctaText: 'Wir zeigen dir passende Profile und die nächsten Schritte für dein Team.',
    experienceValue: '{value} Jahre',
  },
  en: {
    windowTitle: 'Viva Porta Matching · Interactive demo',
    badge: 'Demo mode',
    eyebrow: 'Company portal',
    cobrandLabel: 'in partnership with',
    resultLabel: 'matching profiles',
    searchLabel: 'Search profiles',
    searchPlaceholder: 'Name, qualification or origin',
    filterLabel: 'Focus area',
    resetLabel: 'Reset filters',
    allLabel: 'All',
    emptyTitle: 'No matching profiles',
    emptyText: 'Adjust your search or focus area filter.',
    ctaText: 'We will show you suitable profiles and the next steps for your hiring needs.',
    experienceValue: '{value} years',
  },
  tr: {
    windowTitle: 'Viva Porta Matching · Etkileşimli demo',
    badge: 'Demo modu',
    eyebrow: 'Şirket portalı',
    cobrandLabel: 'iş birliğiyle',
    resultLabel: 'uygun profil',
    searchLabel: 'Profil ara',
    searchPlaceholder: 'Ad, yeterlilik veya köken',
    filterLabel: 'Çalışma alanı',
    resetLabel: 'Filtreleri sıfırla',
    allLabel: 'Tümü',
    emptyTitle: 'Uygun profil bulunamadı',
    emptyText: 'Aramanı veya çalışma alanı filtreni değiştir.',
    ctaText: 'İhtiyacına uygun profilleri ve sonraki adımları birlikte inceleyelim.',
    experienceValue: '{value} yıl',
  },
};
const localizedProfiles = {
  en: [
    [
      'Nurse · recognition in preparation',
      'Nursing',
      'Registered nurse',
      'Bachelor of Nursing',
      'Intensive care, wound care, patient records',
      'Hospital and outpatient care',
    ],
    [
      'Chef · international experience',
      'Hospitality',
      'Chef de partie',
      'Vocational training in hospitality',
      'À la carte, inventory planning, HACCP',
      'Hotels and restaurant groups',
    ],
    [
      'Sales assistant · fresh food counter',
      'Fresh food counter|Retail',
      'Specialist sales assistant',
      'Vocational training in food retail',
      'Customer service, merchandising, hygiene',
      'Fresh food counters and retail',
    ],
    [
      'Butcher · cutting and production',
      'Butchery|Meat cutting',
      'Butcher',
      'Vocational training in meat processing',
      'Meat cutting, production, quality assurance',
      'Craft and production businesses',
    ],
    [
      'Baker · bakery and pastries',
      'Bakery|Bakehouse',
      'Baker',
      'Vocational training in baking',
      'Dough preparation, oven work, pastries',
      'Artisan bakery and production',
    ],
    [
      'Electronics technician · industrial systems',
      'Skilled trades',
      'Electronics technician',
      'Vocational training in electrical engineering',
      'Maintenance, control cabinets, troubleshooting',
      'Industrial and building systems',
    ],
  ],
  tr: [
    [
      'Hemşire · denklik hazırlığında',
      'Hemşirelik',
      'Hemşire',
      'Hemşirelik lisansı',
      'Yoğun bakım, yara bakımı, hasta kayıtları',
      'Hastane ve evde bakım',
    ],
    [
      'Aşçı · uluslararası deneyim',
      'Gastronomi',
      'Kısım şefi',
      'Gastronomi meslek eğitimi',
      'Alakart, stok planlama, HACCP',
      'Otel ve restoran zincirleri',
    ],
    [
      'Satış danışmanı · taze gıda reyonu',
      'Taze gıda reyonu|Perakende',
      'Uzman satış danışmanı',
      'Gıda perakendeciliği meslek eğitimi',
      'Müşteri danışmanlığı, ürün sunumu, hijyen',
      'Taze gıda reyonu ve mağaza satışı',
    ],
    [
      'Kasap · parçalama ve üretim',
      'Kasaplık|Et parçalama',
      'Kasap',
      'Et işleme meslek eğitimi',
      'Parçalama, üretim, kalite kontrol',
      'Zanaat ve üretim işletmeleri',
    ],
    [
      'Fırıncı · fırın ve pastacılık',
      'Fırıncılık|Fırın',
      'Fırıncı',
      'Fırıncılık meslek eğitimi',
      'Hamur hazırlama, fırın işleri, pastacılık',
      'Zanaat fırını ve üretim',
    ],
    [
      'Elektronik teknisyeni · işletme tekniği',
      'Zanaat',
      'Elektronik teknisyeni',
      'Elektrik meslek eğitimi',
      'Bakım, pano yapımı, arıza tespiti',
      'Endüstriyel tesisler ve bina sistemleri',
    ],
  ],
};

export const createMatchingContent = (locale: LandingLocale): LandingMatchingContent => ({
  ...ui[locale],
  companyName: 'Musterpflege Rhein-Main GmbH',
  initials: 'MR',
  logoUrl: '',
  logoAlt: 'Musterpflege Rhein-Main GmbH',
  cobrandLogoUrl: brandAssetRepository.navbarLogoDataUri,
  cobrandLogoAlt: 'Viva Porta',
  labels: labels(locale),
  candidates: candidates.map((candidate, index) => {
    if (locale === 'de') return structuredClone(candidate);
    const [qualification, areas, currentProfession, education, skills, experience] = localizedProfiles[locale][index]!;
    return {
      ...candidate,
      qualification,
      focusAreas: areas!.split('|'),
      currentProfession,
      education,
      skills,
      experience,
      origin: 'Türkiye',
      documents: candidate.documents?.map(document => ({
        ...document,
        label:
          document.id === 'cv'
            ? locale === 'en'
              ? 'CV'
              : 'Özgeçmiş'
            : locale === 'en'
              ? 'Qualification certificate'
              : 'Yeterlilik belgesi',
      })),
    };
  }),
});
